// src/context/ThemeContext.js
//
// Fase 5 Redeprog SaaS: toggle dark/light + branding global por Organizacion.
//
// Nuevo (03-oct-26):
//   • En modo Fenagro, el tema global ahora usa fenagroTheme (ya existente
//     para el Home público) en todo el dashboard, no solo en el landing.
//     Antes el dashboard quedaba con el tema azul oscuro de EducaT; ahora
//     se ve consistente con la identidad verde institucional de Fenagro.
//
//   • Expone productoInfo (nombre/nombreCorto/etiquetaLogo) resuelto por
//     organización/modo. Los componentes muestran "Aulas Virtuales" en
//     Fenagro y "EducaT" en los demás deploys, sin hardcoded.
//
//   • Setea document.title dinámico según productoInfo.nombre.
//
// Comportamiento por modo:
//   Fenagro  → fenagroTheme global, productoInfo = "Aulas Virtuales".
//   Redeprog → tema dinámico desde createAppTheme + branding de org.

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createAppTheme } from '../theme';
import fenagroTheme from '../theme/fenagroTheme';
import { resolveProducto } from '../theme/productoResolver';
import { organizacionService, IS_REDEPROG, MODO_OPERACION } from '../services/api';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [organizacion, setOrganizacion] = useState(null);

  // Fase 5: cargar Organizacion activa al montar. Silencioso si falla.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const org = await organizacionService.getActual();
        if (!cancelled && org) setOrganizacion(org);
      } catch (e) {
        // Si el endpoint no responde (backend caído, sin red, etc.),
        // la app sigue con el tema default. No es un error fatal.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Nombre del producto (Aulas Virtuales / EducaT / ComuniSer / etc.)
  const productoInfo = useMemo(
    () => resolveProducto({ modoOperacion: MODO_OPERACION, organizacion }),
    [organizacion]
  );

  // Título del tab dinámico — "Aulas Virtuales | Fenagro", "EducaT", etc.
  useEffect(() => {
    const nombreOrg = organizacion?.nombre;
    document.title = nombreOrg
      ? `${productoInfo.nombre} · ${nombreOrg}`
      : productoInfo.nombre;
  }, [productoInfo.nombre, organizacion]);

  // Theme global:
  //   Fenagro  → fenagroTheme (verde institucional) para TODA la app.
  //   Redeprog → tema dinámico con branding por organización.
  const theme = useMemo(() => {
    if (!IS_REDEPROG) {
      // Modo Fenagro: tema institucional global, no se altera por dark toggle
      // (el fenagroTheme está pensado solo en modo light).
      return fenagroTheme;
    }
    return createAppTheme(isDarkMode, organizacion);
  }, [isDarkMode, organizacion]);

  const toggleTheme = () => setIsDarkMode(prev => !prev);

  const value = {
    isDarkMode,
    theme,
    toggleTheme,
    organizacion,     // { id, nombre, slug, logoUrl, colorPrimario, ... } o null
    productoInfo,     // { nombre, nombreCorto, etiquetaLogo } — nunca null
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
